import clsx from "clsx";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MoveWidget } from "../../../assets";
import { useSettings } from "../../../context/SettingsContext";

interface WidgetToggleProps {
  moveFlyoutToRight: () => any;
  moveFlyoutToLeft: () => any;
  isFlyoutOpen: any;
  isDragging: any;
}
const WidgetToggle: React.FC<WidgetToggleProps> = ({
  moveFlyoutToRight,
  moveFlyoutToLeft,
  isFlyoutOpen,
  isDragging,
}) => {
  const { setIsRight, isRight, buttonColor, buttonTextColor } =
    useSettings() as {
      setIsRight: React.Dispatch<React.SetStateAction<boolean>>;
      isRight: boolean;
      buttonColor: string;
      buttonTextColor: string;
    };
  const { oversizeWidget } = useSettings();
  const [t] = useTranslation();

  // Use a ref to track if we're in the middle of a toggle transition
  const isToggling = useRef(false);
  // Create another ref for the initial render check
  const initialRender = useRef(true);
  // Create a variable to track if the position was just adjusted via drag
  const wasDragging = useRef(false);
  const togglePosition = () => {
    // Prevent multiple toggles while a toggle is in progress
    if (isToggling.current) return;

    isToggling.current = true;

    // Provide immediate visual feedback of toggle action
    const toggleButton = document.activeElement as HTMLElement;
    if (toggleButton) {
      toggleButton.style.transform = "scale(0.95)";
      setTimeout(() => {
        if (toggleButton) toggleButton.style.transform = "scale(1)";
      }, 150);
    }

    setIsRight((prev: boolean): boolean => {
      // Toggle change will be handled by useEffect that watches isRight
      return !prev;
    });

    // Reset the toggle lock after the transition is complete
    setTimeout(() => {
      isToggling.current = false;
    }, 300); // Match transition duration
  };

  // Smooth transition effect when position changes  // This useEffect detects when isDragging changes from true to false
  useEffect(() => {
    // When dragging ends, set the flag to remember we just finished dragging
    if (!isDragging && wasDragging.current) {
      // Keep the wasDragging flag true for a short period to prevent
      // the position-setting effect from moving the flyout
      setTimeout(() => {
        wasDragging.current = false;
      }, 500);
    }

    // When dragging starts, set the flag
    if (isDragging) {
      wasDragging.current = true;
    }
  }, [isDragging]);

  // This useEffect handles the position changes based on isRight toggle
  useEffect(() => {
    // If currently dragging or just finished dragging, skip position updates
    if (isDragging || wasDragging.current) {
      return;
    }

    // Handle initial render separately to avoid unnecessary animations
    if (initialRender.current && isFlyoutOpen) {
      initialRender.current = false;
      if (isRight) {
        moveFlyoutToRight();
      } else {
        moveFlyoutToLeft();
      }
      return;
    }

    // For toggle actions, use a small delay to ensure the transition is smooth
    const timeoutId = setTimeout(() => {
      if (isRight) {
        moveFlyoutToRight();
      } else {
        moveFlyoutToLeft();
      }
    }, 10); // Small delay for smoother transition

    // Cleanup timeout on unmount or when dependencies change
    return () => clearTimeout(timeoutId);
  }, [isRight, isFlyoutOpen, moveFlyoutToLeft, moveFlyoutToRight, isDragging]);

  return (
    <div className="hidden sm:flex px-2 py-2 rounded-lg transition bg-white mx-2 flex-col items-center space-y-3">
      <div className="flex items-center space-x-2">
        <div className="w-7 h-7  rounded-full flex items-center justify-center">
          {/* <img src={movewidget} alt="Widget Icon" className="w-7 h-6" /> */}
          <MoveWidget />
        </div>
        <p
          className={clsx(
            "text-sm font-medium text-gray-800",
            oversizeWidget && "font-semibold"
          )}
        >
          {t("move_widget")}
        </p>
      </div>
      {/* Toggle section */}
      <div className="flex items-center justify-between w-full px-4 py-2 bg-blue-50 rounded-lg">
        <span
          className={clsx(
            "text-sm font-medium",
            !isRight ? "text-black" : "text-gray-500"
          )}
        >
          {t("left")}
        </span>
        <button
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              togglePosition();
            }
          }}
          tabIndex={0}
        >
          <div
            className={`w-14 h-7 flex items-center bg-white rounded-full p-1 cursor-pointer ${
              isRight ? "justify-end" : "justify-start"
            }`}
            onClick={togglePosition}
            style={{
              backgroundColor: buttonColor,
              transition: "background-color 0.3s ease",
            }}
          >
            {" "}
            <div
              className="w-5 h-5 bg-white rounded-full shadow-md"
              style={{
                //backgroundColor: buttonTextColor,
                transition: "all 0.3s ease-in-out",
                transform: `scale(${isToggling.current ? "0.9" : "1"})`, // Add a subtle scale effect during transition
              }}
            ></div>
          </div>
        </button>
        <span
          className={clsx(
            "text-sm font-medium",
            isRight ? "text-black" : "text-gray-500"
          )}
        >
          {t("right")}
        </span>
      </div>
    </div>
  );
};

export default WidgetToggle;
